import { commitLocalUpdate } from "react-relay";
import { getRequest, createOperationDescriptor, graphql } from "relay-runtime";

import { getConfigs, onConfigsLoaded, type Configs } from "app/utils/configs";
import { RelayEnvironment as environment } from "RelayEnvironment";

import type { Environment } from "react-relay";

export function setIsOnboardingSkipped(skipped: boolean, id: string): void {
  commitLocalUpdate(environment, (store) => {
    const whoami = store.getRoot().getLinkedRecord("whoami");
    const dataID = `client:Config:${id}`;
    const config = store.create(dataID, "Config");
    config.setValue(skipped, "isOnboardingSkipped");
    whoami?.setLinkedRecord(config, "config");
  });
}

function _urlsFor(configs: Configs): Record<string, string | undefined> {
  return {
    docs: configs.docs?.url,
    jun0: configs.jun0?.url,
    mcp: configs.jun0?.mcp_url,
    redash: configs.redash?.url,
    sinistral: configs.sinistral?.url,
    superset: configs.superset?.url,
  };
}

function _writeUrlConfig(environment: Environment, configs: Configs) {
  const urls = _urlsFor(configs);

  // Updating Relay Store
  commitLocalUpdate(environment, (store) => {
    const fieldKey = "UrlConfig";
    const __typename = "UrlConfig";

    const dataID = `client:${__typename}`;
    const existing = store.get(dataID);
    const record = existing ?? store.create(dataID, __typename);

    // A read whose optional file failed reports the URL as absent. Keep what the
    // record already holds in that case: a link that works must not disappear
    // under a user because one request failed. Relay ignores a write that
    // changes nothing, so an unchanged URL notifies no reader.
    Object.entries(urls).forEach(([field, url]) => {
      if (url !== undefined || !existing) {
        record.setValue(url, field);
      }
    });

    if (existing) {
      return;
    }

    const localDataQuery = graphql`
      query localStateQuery {
        UrlConfig {
          __typename
        }
      }
    `;

    const request = getRequest(localDataQuery);
    const operation = createOperationDescriptor(request, {});
    environment.retain(operation);

    store.getRoot().setLinkedRecord(record, fieldKey);
  });
}

const followedEnvironments = new WeakSet<Environment>();

export async function createUrlConfig(environment: Environment) {
  _writeUrlConfig(environment, await getConfigs());

  // An optional config that failed during bootstrap reads as undefined, and a
  // later read may recover it. The record follows every read from here on, so a
  // recovered feature appears without a reload. One listener per environment is
  // enough, and a repeat call must not stack another.
  if (!followedEnvironments.has(environment)) {
    followedEnvironments.add(environment);
    onConfigsLoaded((configs) => _writeUrlConfig(environment, configs));
  }
}
